import { type DpopMode, DpopUnavailableError } from './dpop-policy';

/**
 * The header choice behind buildAuthorizationHeaders (dpop-authorization.ts), with every dependency
 * passed in so it loads and runs without the auth services (tests/probes/dpop-modules.spec.ts).
 */

export type AuthorizationHeaders = { Authorization: string; DPoP?: string };

export interface HeaderInputs {
    method: string | undefined;
    resolveUrl: () => string;
    b2cAccessToken: string | null;
    isRetry: boolean;
    mode: DpopMode;
    strict: boolean;
    gatewayOriginOf: (url: string) => string | null;
    invalidateToken: () => void;
    getToken: (gatewayOrigin: string, b2cAccessToken: string) => Promise<string | null>;
    createProof: (method: string, url: string, accessToken: string) => Promise<string>;
}

/**
 * DPoP headers when the session uses DPoP and the request goes to the gateway; otherwise the Bearer
 * header the app has always sent. Without strict mode it never throws - any failure means Bearer.
 * In strict mode a gateway request never falls back to the B2C token: it throws DpopUnavailableError.
 */
export async function chooseAuthorizationHeaders(i: HeaderInputs): Promise<AuthorizationHeaders> {
    // Interpolated exactly as the interceptor always did, so the fallback is byte-for-byte unchanged.
    const bearer: AuthorizationHeaders = { Authorization: `Bearer ${i.b2cAccessToken}` };

    // Checked first, so with DPoP off nothing else here runs - not even resolving the URL.
    if (!i.b2cAccessToken || i.mode === 'off') return bearer;

    let gatewayOrigin: string | null;
    try {
        const url = i.resolveUrl();
        gatewayOrigin = i.gatewayOriginOf(url);
        // Blob storage, R2, Google Maps and every other host keep their own credentials.
        if (!gatewayOrigin) return bearer;
        if (i.mode === 'unavailable') throw new DpopUnavailableError('This browser cannot hold a DPoP key.');

        // A retry follows a 401. If it was our token the gateway rejected, the held one is useless;
        // exchanging again is cheap, and harmless if the 401 had another cause.
        if (i.isRetry) i.invalidateToken();

        const token = await i.getToken(gatewayOrigin, i.b2cAccessToken);
        if (!token) {
            if (i.strict) throw new DpopUnavailableError();
            return bearer;
        }

        const proof = await i.createProof(i.method ?? 'GET', url, token);
        return { Authorization: `DPoP ${token}`, DPoP: proof };
    } catch (error) {
        if (i.strict) throw error instanceof DpopUnavailableError ? error : new DpopUnavailableError();
        console.warn('DPoP could not be applied to this request; sending Bearer.', error);
        return bearer;
    }
}
